import React, { useState, useEffect } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { Settings, Save, RefreshCw, Info } from 'lucide-react';

function Impostazioni() {
  const [impostazioni, setImpostazioni] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({});

  useEffect(() => {
    loadImpostazioni();
  }, []);

  const loadImpostazioni = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/impostazioni', {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setImpostazioni(response.data.data);
        // Prepopola form
        const data = {};
        response.data.data.forEach(imp => {
          data[imp.chiave] = imp.valore;
        });
        setFormData(data);
      }
    } catch (error) {
      console.error('Errore caricamento impostazioni:', error);
      toast.error('Errore nel caricamento delle impostazioni');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (chiave, valore) => {
    setFormData(prev => ({
      ...prev,
      [chiave]: valore
    }));
  };

  const handleSave = async (chiave) => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(
        `/api/impostazioni/${chiave}`,
        { valore: formData[chiave] },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        toast.success('Impostazione aggiornata');
        loadImpostazioni();
      }
    } catch (error) {
      console.error('Errore salvataggio:', error);
      toast.error('Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const renderInput = (impostazione) => {
    const { chiave, tipo } = impostazione;
    const valore = formData[chiave] || '';

    switch (tipo) {
      case 'number':
        return (
          <input
            type="number"
            value={valore}
            onChange={(e) => handleChange(chiave, e.target.value)}
            min="1"
            max="20"
            className="w-32 px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
          />
        );
      case 'boolean':
        return (
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={valore === 'true'}
              onChange={(e) => handleChange(chiave, e.target.checked ? 'true' : 'false')}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
          </label>
        );
      default:
        return (
          <input
            type="text"
            value={valore}
            onChange={(e) => handleChange(chiave, e.target.value)}
            className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
          />
        );
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Impostazioni</h1>
        <p className="mt-1 text-sm text-gray-500">
          Configura il comportamento dell'applicazione
        </p>
      </div>

      {/* Sezione AI Suggerimenti */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-medium text-gray-900 flex items-center">
            <Settings className="h-5 w-5 mr-2 text-blue-600" />
            Suggerimenti AI
          </h2>
        </div>

        <div className="px-6 py-4 space-y-4">
          {impostazioni
            .filter(imp => imp.chiave.startsWith('ai_'))
            .map((impostazione) => (
              <div key={impostazione.chiave} className="flex flex-col sm:flex-row sm:items-center sm:justify-between py-3 border-b border-gray-100 last:border-0 gap-3">
                <div className="flex-1">
                  <label className="text-sm font-medium text-gray-700">
                    {impostazione.descrizione}
                  </label>
                  <p className="text-xs text-gray-500 mt-1">
                    Chiave: <code className="bg-gray-100 px-1 py-0.5 rounded text-xs">{impostazione.chiave}</code>
                  </p>
                </div>
                <div className="flex items-center space-x-3">
                  {renderInput(impostazione)}
                  <button
                    onClick={() => handleSave(impostazione.chiave)}
                    disabled={saving}
                    className="btn btn-primary btn-sm flex-shrink-0"
                    title="Salva modifiche"
                  >
                    <Save className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
        </div>
      </div>

      {/* Info Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex">
          <Info className="h-5 w-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" />
          <div>
            <h3 className="text-sm font-medium text-blue-900">
              Come funzionano i suggerimenti AI
            </h3>
            <p className="mt-1 text-sm text-blue-700">
              Il sistema analizza gli ultimi <strong>{formData.ai_giorni_analisi || 6}</strong> ordini 
              dello stesso giorno della settimana e suggerisce prodotti ordinati almeno{' '}
              <strong>{formData.ai_soglia_minima || 2}</strong> volte.
            </p>
            <p className="mt-2 text-sm text-blue-700">
              I suggerimenti sono visibili solo agli <strong>amministratori</strong> nella pagina "Nuovo Ordine".
            </p>
          </div>
        </div>
      </div>

      {/* Esempio pratico */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <div className="flex">
          <RefreshCw className="h-5 w-5 text-gray-600 mr-3 mt-0.5 flex-shrink-0" />
          <div>
            <h3 className="text-sm font-medium text-gray-900">
              Esempio pratico
            </h3>
            <p className="mt-1 text-sm text-gray-600">
              Se crei un ordine per <strong>Giovedì</strong>, il sistema guarderà gli ultimi{' '}
              <strong>{formData.ai_giorni_analisi || 6} Giovedì</strong> e ti suggerirà:
            </p>
            <ul className="mt-2 text-sm text-gray-600 list-disc list-inside space-y-1">
              <li>Prodotti ordinati in {formData.ai_soglia_minima || 2} o più di quei giovedì</li>
              <li>Quantità media di quei prodotti</li>
              <li>Ultime note usate per quel prodotto</li>
              <li>Fornitore preferito per ogni prodotto</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Impostazioni;

